// skipcq: JS-W1028
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { captureBeerIngestionReviewOpen } from '../../utils/beerIngestionAnalytics';
import { formatIngestionRunTimestamp } from '../../utils/brewery/beerIngestionRunFilters';
import {
  inboxActionLabel,
  inboxProgressLabel,
  reviewChangeSummaryLabel,
} from '../../utils/brewery/beerIngestionReview';
import {
  breweryNamesFromQueueRows,
  buildBeerIngestionReviewPath,
  queueRunIdsFromRows,
} from '../../utils/brewery/beerIngestionReviewQueue';
import { BreweryNameCell } from './beerIngestionPanels';

function InboxTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Brewery</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>Started</TableCell>
        <TableCell>Changes</TableCell>
        <TableCell>Progress</TableCell>
        <TableCell>Action</TableCell>
      </TableRow>
    </TableHead>
  );
}

function isSafeHttpUrl(sourceUrl) {
  try {
    const parsed = new URL(sourceUrl);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function SourceLink({ sourceUrl }) {
  if (!isSafeHttpUrl(sourceUrl)) {
    return '—';
  }
  return (
    <Link href={sourceUrl} target="_blank" rel="noopener noreferrer">
      {sourceUrl}
    </Link>
  );
}

SourceLink.propTypes = {
  sourceUrl: PropTypes.string,
};

function InboxRow({ row, onReviewClick }) {
  return (
    <TableRow>
      <TableCell>
        <BreweryNameCell name={row.breweryName} breweryId={row.breweryId} openInNewTab />
      </TableCell>
      <TableCell>
        <SourceLink sourceUrl={row.sourceUrl} />
      </TableCell>
      <TableCell>{formatIngestionRunTimestamp(row.startedAt)}</TableCell>
      <TableCell>{reviewChangeSummaryLabel(row)}</TableCell>
      <TableCell>{inboxProgressLabel(row)}</TableCell>
      <TableCell>
        <Button
          size="small"
          data-run-id={row.runId}
          onClick={onReviewClick}
        >
          {inboxActionLabel(row)}
        </Button>
      </TableCell>
    </TableRow>
  );
}

InboxRow.propTypes = {
  row: PropTypes.object.isRequired,
  onReviewClick: PropTypes.func.isRequired,
};

export default function BeerIngestionReviewInbox({
  rows,
  loading,
  error,
  stateCode,
}) {
  const navigate = useNavigate();

  const emptyMessage = useMemo(() => {
    if (loading) {
      return null;
    }
    if (rows.length === 0) {
      return 'No beer changes waiting for review or apply in this state.';
    }
    return null;
  }, [loading, rows.length]);

  const handleReviewClick = useCallback((event) => {
    const runId = event.currentTarget.getAttribute('data-run-id');
    const row = rows.find((item) => item.runId === runId);
    if (!runId) {
      return;
    }
    captureBeerIngestionReviewOpen(runId, row?.breweryId, 'inbox');
    navigate(buildBeerIngestionReviewPath(runId, stateCode), {
      state: {
        queueRunIds: queueRunIdsFromRows(rows),
        breweryNames: breweryNamesFromQueueRows(rows),
      },
    });
  }, [navigate, rows, stateCode]);

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} aria-label="Loading review inbox" />
        </Box>
      ) : null}
      {!loading && emptyMessage ? (
        <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
      ) : null}
      {!loading && rows.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Beer ingestion review inbox">
            <InboxTableHead />
            <TableBody>
              {rows.map((row) => (
                <InboxRow
                  key={row.runId}
                  row={row}
                  onReviewClick={handleReviewClick}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
}

BeerIngestionReviewInbox.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  stateCode: PropTypes.string,
};
